import { InjectionToken } from '@angular/core';
import { registerPlugin, WebPlugin } from '@capacitor/core';

/**
 * TypeScript contract for the Swift NativeCallPlugin bridge.
 *
 * On an iOS Role Play call the native Daily client, not the WebView, owns the
 * learner's mic, camera and screen. The web app drives it through these
 * methods and learns the resulting state from their return values.
 *
 * At runtime Capacitor routes each call:
 *   - Inside the native iOS shell → Swift NativeCallPlugin
 *   - In a browser               → NativeCallWeb fallback below
 */
export interface NativeCallPlugin {
  /** Joins the room and publishes camera and mic. The mic starts muted. */
  join(options: { url: string; token?: string }): Promise<NativeCallMediaState>;
  /** Leaves the room. Resolves even when no call is joined. */
  leave(): Promise<void>;
  /** Sets the mic on or off (an explicit set, not a toggle). Resolves with the applied state. */
  setMicrophoneEnabled(options: { enabled: boolean }): Promise<{ microphoneEnabled: boolean }>;
  /**
   * Sets the camera on or off (an explicit set, not a toggle). Turning it off also
   * turns off the camera device and its indicator. Resolves with the applied state.
   */
  setCameraEnabled(options: { enabled: boolean }): Promise<{ cameraEnabled: boolean }>;
  /** Screen share IDs and whether the App Group and broadcast extension are wired up. */
  getScreenShareConfig(): Promise<NativeScreenShareConfig>;
}

export interface NativeCallMediaState {
  microphoneEnabled: boolean;
  cameraEnabled: boolean;
}

export interface NativeScreenShareConfig {
  appGroupId: string;
  extensionBundleId: string;
  appGroupAvailable: boolean;
  extensionEmbedded: boolean;
}

/**
 * Web fallback — keeps browsers working without native code.
 * Nothing native exists in a browser, so every method resolves harmlessly:
 * setters report the requested state and the call never joins.
 */
class NativeCallWeb extends WebPlugin implements NativeCallPlugin {
  async join(): Promise<NativeCallMediaState> {
    return { microphoneEnabled: false, cameraEnabled: false };
  }
  async leave(): Promise<void> {}
  async setMicrophoneEnabled({ enabled }: { enabled: boolean }): Promise<{ microphoneEnabled: boolean }> {
    return { microphoneEnabled: enabled };
  }
  async setCameraEnabled({ enabled }: { enabled: boolean }): Promise<{ cameraEnabled: boolean }> {
    return { cameraEnabled: enabled };
  }
  async getScreenShareConfig(): Promise<NativeScreenShareConfig> {
    return { appGroupId: '', extensionBundleId: '', appGroupAvailable: false, extensionEmbedded: false };
  }
}

export const NativeCall = registerPlugin<NativeCallPlugin>('NativeCall', {
  web: () => new NativeCallWeb(),
});

/**
 * DI handle for the plugin, so services can be tested with a fake. Capacitor's
 * plugin object is a Proxy that can't be spied on directly.
 */
export const NATIVE_CALL_PLUGIN = new InjectionToken<NativeCallPlugin>('NativeCallPlugin', {
  providedIn: 'root',
  factory: () => NativeCall,
});
